import { Link } from "@tanstack/react-router";
import { ShieldAlert } from "lucide-react";
import { KpiCard, Panel } from "@/lovable-demo/components/bits";
import { EscStatusPill, LevelPill, SeverityPill } from "@/lovable-demo/components/escalations";
import {
  dueLabel,
  fmtDateTime,
  isAttention,
  isOpen,
  isResponseOverdue,
  isOverdue,
  isRecentlyDecided,
  STATUS_LABEL,
  useEscalations,
  type Escalation,
} from "@/lovable-demo/lib/governance/escalations";
import { fmtCompactCurrency } from "@/lovable-demo/lib/warranty/calc";
import { classifySystem } from "@/lovable-demo/lib/warranty/safety";
import type { ProblemRecord } from "@/lovable-demo/lib/warranty/types";
import { cn } from "@/lib/utils";

/**
 * Executive Dashboard lens over the governed escalations. Reads the same
 * escalation rows as the Problem Record, Executive Summary, Daily Briefing and
 * Financial Intelligence — no second workflow, no copy, no writes. Ordered by
 * due date and criticality, never by dollars; exposure informs, it does not
 * rank the decision.
 */

function safetyFlag(e: Escalation, r: ProblemRecord): string | null {
  const sys = classifySystem(r);
  if (e.safety_regulatory || /safety|regulat|compliance/i.test(e.trigger_key))
    return sys.fmvss !== "—" ? `Safety · ${sys.fmvss}` : "Safety / regulatory trigger";
  return sys.fmvss !== "—" ? `Regulated system · ${sys.fmvss}` : null;
}

/** Governance urgency order: past due first, then critical, then by due date. */
function urgency(e: Escalation): number {
  if (isResponseOverdue(e) || (e.safety_regulatory && e.level >= 3)) return 0;
  if (isOverdue(e)) return 1;
  if (e.level >= 3 || e.severity === "critical") return 2;
  return 3;
}
const byUrgency = (a: Escalation, b: Escalation) =>
  urgency(a) - urgency(b) || (a.due_at ?? "9").localeCompare(b.due_at ?? "9");

function Row({ e, r }: { e: Escalation; r: ProblemRecord }) {
  const safety = safetyFlag(e, r);
  return (
    <li className="py-3">
      <div className="flex flex-wrap items-center gap-2">
        <Link
          to="/demo/problem-record/$id"
          params={{ id: e.record_id }}
          hash="decision-queue"
          className="hover:text-primary"
        >
          <span className="num text-xs font-semibold text-primary">{e.record_code}</span>{" "}
          <span className="text-sm">{r.title}</span>
        </Link>
        <LevelPill e={e} />
        <SeverityPill e={e} />
        <EscStatusPill e={e} />
        {safety && (
          <span className="inline-flex items-center gap-1 rounded border border-destructive/50 px-1.5 py-0.5 text-[0.62rem] font-semibold uppercase tracking-wider text-destructive">
            <ShieldAlert className="size-3" /> {safety}
          </span>
        )}
      </div>
      <p className="mt-1 text-xs text-foreground/85">
        <span className="text-muted-foreground">Decision required:</span>{" "}
        {e.decision_required || e.reason}
      </p>
      <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <span className="text-muted-foreground">
          Authority: <span className="text-foreground/85">{e.escalated_to_label}</span>
        </span>
        <span
          className={cn(
            "num",
            isOverdue(e) ? "font-semibold text-destructive" : "text-muted-foreground",
          )}
        >
          {e.status === "decided" ? `Decided ${fmtDateTime(e.decided_at)}` : dueLabel(e)}
        </span>
        <span className="num text-foreground/85">
          Exposure <span className="font-semibold">{fmtCompactCurrency(r.financial.exposure)}</span>
        </span>
      </div>
    </li>
  );
}

export function DashboardEscalations({ records }: { records: ProblemRecord[] }) {
  const { escalations, loading } = useEscalations();
  const byId = new Map(records.map((r) => [r.id, r]));
  const inScope = escalations.filter((e) => byId.has(e.record_id));
  const open = inScope.filter(isOpen).sort(byUrgency);
  const critical = open.filter((e) => e.severity === "critical" || isOverdue(e));
  const awaiting = open.filter((e) => e.status === "awaiting_decision");
  const exposure = open.reduce((a, e) => a + byId.get(e.record_id)!.financial.exposure, 0);
  const recent = inScope.filter((e) => isRecentlyDecided(e, 14));
  const attention = inScope.filter(isAttention);
  const level3 = open.filter((e) => e.level >= 3).length;

  return (
    <Panel
      title="Management Escalations"
      subtitle="Decision points requiring management attention — from the governed escalation records on each Problem Record"
    >
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <KpiCard label="Open escalations" value={String(open.length)} />
            <KpiCard
              label="Critical / past due"
              value={String(critical.length)}
              tone={critical.length ? "bad" : "good"}
            />
            <KpiCard
              label="Awaiting decision"
              value={String(awaiting.length)}
              tone={awaiting.length ? "warn" : "good"}
            />
            <KpiCard
              label="Open escalation exposure"
              value={fmtCompactCurrency(exposure)}
              hint="Actual + projected on escalated records"
            />
          </div>

          <p className="text-xs text-muted-foreground">
            <span className="num font-semibold text-risk-critical">{level3}</span> Level 3 critical
            / executive ·{" "}
            <span className="num font-semibold text-foreground">{open.length - level3}</span> Level
            2 management · <span className="num font-semibold text-info">{attention.length}</span>{" "}
            Level 1 attention items being monitored
          </p>
          {open.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No management escalation is open in this view.
            </p>
          ) : (
            <ul className="divide-y divide-border/60">
              {open.slice(0, 5).map((e) => (
                <Row key={e.id} e={e} r={byId.get(e.record_id)!} />
              ))}
            </ul>
          )}
          {open.length > 5 && (
            <details>
              <summary className="cursor-pointer text-xs font-semibold text-primary">
                Show all open escalations ({open.length})
              </summary>
              <ul className="mt-2 divide-y divide-border/60">
                {open.slice(5).map((e) => (
                  <Row key={e.id} e={e} r={byId.get(e.record_id)!} />
                ))}
              </ul>
            </details>
          )}

          {recent.length > 0 && (
            <details>
              <summary className="cursor-pointer text-xs font-semibold text-primary">
                Recently decided ({recent.length} · last 14 days)
              </summary>
              <ul className="mt-2 divide-y divide-border/60">
                {recent.map((e) => (
                  <li key={e.id} className="flex flex-wrap items-center gap-2 py-2 text-xs">
                    <Link
                      to="/demo/problem-record/$id"
                      params={{ id: e.record_id }}
                      hash="decision-queue"
                      className="num font-semibold text-primary hover:underline"
                    >
                      {e.record_code}
                    </Link>
                    <span className="text-foreground/85">{e.manager_decision}</span>
                    <span className="text-muted-foreground">
                      {STATUS_LABEL[e.status] ?? e.status} · decided {fmtDateTime(e.decided_at)} ·{" "}
                      {e.decided_by_label}
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          )}

          <p className="text-[0.7rem] text-muted-foreground">
            Ordered by safety/regulatory override, due date, level and governance urgency — not by
            dollars. Financial exposure informs the decision; the named authority on the Problem
            Record makes it.
          </p>
        </div>
      )}
    </Panel>
  );
}
